<template>
  <slice
    :id="block._key"
    class="slice--simple-content container"
    :class="{
      'slice--right-img': block.image_position === 'right',
      'image-in-screen': imageInScreen,
      'slice--no-gap': !block.gap,
    }"
    @in="$emit('in')"
    @out="$emit('out')"
  >
    <div
      class="row-md row-hard align-center"
      :class="{
        'justify-end':
          block.image_position === 'right' &&
          block.image_format === IMAGE_FORMAT.PORTRAIT,
      }"
    >
      <div
        v-waypoint="{
          active: true,
          callback: onWaypoint,
          options: intersectionOptions,
        }"
        class="col-md-6 slice__img mb-2 mb-md-0"
        :class="`slice__img--${block.image_format}`"
        :style="
          hasImageBackgroundColor
            ? {
                background: block.image_background_color,
              }
            : ''
        "
      >
        <lazy-image
          v-if="image && image.url"
          :image="image"
          :sizes="sizes"
          :widths="widths"
          provider="sanity"
          class="flex-grow-1"
        />
      </div>
      <div class="d-md-flex align-center slice__text" :class="blockTextClass">
        <div
          class="block-text"
          :class="{ 'pt-md-2 pb-md-2': hasImageBackgroundColor }"
        >
          <portable-text :blocks="block.text" class="block-text__text" />
        </div>
      </div>
    </div>
  </slice>
</template>

<script>
import Slice from '@/components/Slices/Slice'
import LazyImage from '@/components/Image/LazyImage'
import PortableText from '@/components/RichText/PortableText'

import { IMAGE_FORMAT } from '@/const'
import { getSanityBunnyImageLink } from '@/utils/image'

export default {
  name: 'BlockSimpleContent',
  components: {
    Slice,
    LazyImage,
    PortableText,
  },
  props: {
    block: { type: Object, required: true },
  },
  data() {
    return {
      IMAGE_FORMAT,
      imageInScreen: false,
      intersectionOptions: {
        root: null,
        rootMargin: '0px 0px 0px 0px',
        threshold: [0.25, 1], // [0.25, 0.75] if you want a 25% offset!
      },
    }
  },
  computed: {
    hasText() {
      return this.block.text.length > 0
    },
    hasImageBackgroundColor() {
      return (
        this.block.image_background_color &&
        this.block.image_background_color !== ''
      )
    },
    image() {
      switch (this.block.image_format) {
        case IMAGE_FORMAT.SQUARE:
          return {
            url: getSanityBunnyImageLink(this.block.image, {
              width: 720,
              height: 720,
            }),
            dimensions: {
              width: 720,
              height: 720,
            },
          }
        case IMAGE_FORMAT.LANDSCAPE:
          return {
            url: getSanityBunnyImageLink(this.block.image, {
              width: 600,
              height: 440,
            }),
            dimensions: {
              width: 600,
              height: 440,
            },
          }
        default:
          return {
            url: getSanityBunnyImageLink(this.block.image, {
              width: 480,
              height: 640,
            }),
            dimensions: {
              width: 480,
              height: 640,
            },
          }
      }
    },
    sizes() {
      switch (this.block.image_format) {
        case IMAGE_FORMAT.SQUARE:
          return '720px'
        case IMAGE_FORMAT.LANDSCAPE:
          return '600px'
        default:
          return '480px'
      }
    },
    widths() {
      switch (this.block.image_format) {
        case IMAGE_FORMAT.SQUARE:
          return [360, 720, 1440]
        case IMAGE_FORMAT.LANDSCAPE:
          return [300, 600, 1200]
        default:
          return [240, 480, 960]
      }
    },
    blockTextClass() {
      const cc = []

      if (this.block.gap) {
        cc.push('col-md-4 col-md-push-1')
      } else {
        cc.push('col-md-6')
      }

      if (
        (this.block.image_format !== IMAGE_FORMAT.PORTRAIT ||
          this.block.image_position === 'right') &&
        this.block.gap
      ) {
        cc.push('col-md-offset-1')
      }

      return cc
    },
  },
  methods: {
    onWaypoint({ going }) {
      // going: in, out
      // direction: top, right, bottom, left
      if (going === this.$waypointMap.GOING_IN) {
        this.imageInScreen = true
      }

      if (going === this.$waypointMap.GOING_OUT) {
        this.imageInScreen = false
      }
    },
  },
}
</script>

<style lang="scss">
.slice--simple-content {
  transition: padding 0.6s;

  .slice__text {
    transition: padding 0.6s;

    .block-text {
      max-width: 100%;
    }
  }

  .slice__img {
    align-self: stretch;
    display: flex;
    align-items: center;

    &--portrait {
      .lazy-image-container {
        width: auto;
        max-width: rem(480px);
        margin-left: auto;
        margin-right: auto;
      }
    }

    &--square {
      .lazy-image-container {
        width: auto;
        max-width: rem(720px);
        margin-right: auto;
      }
    }

    &--paysage {
      .lazy-image-container {
        width: auto;
        max-width: rem(600px);
        margin-left: auto;
      }
    }

    &--svg {
      img {
        width: rem(120px);
        height: rem(120px);
      }
    }
  }

  &.image-in-screen {
    @include mq($until: tablet) {
      padding-left: 0;
      padding-right: 0;

      &:not(.slice--no-gap) {
        .slice__text {
          padding-left: var(--gutter);
          padding-right: var(--gutter);
        }
      }
    }
  }

  &.slice--right-img {
    @include mq($from: tablet) {
      .slice__img {
        order: 2;
      }

      .slice__text {
        order: 1;
      }
    }

    .slice__img {
      &--square {
        .lazy-image-container {
          margin-left: auto;
          margin-right: 0;
        }
      }

      &--paysage {
        .lazy-image-container {
          margin-left: 0;
          margin-right: auto;
        }
      }
    }
  }

  &.slice--no-gap {
    .slice__img {
      margin-bottom: 0;
    }

    &.slice--in-screen {
      padding-left: 0;
      padding-right: 0;
    }
  }

  &.slice--in-screen:not(.slice--no-gap) {
    .slice__text {
      .lazy-image-container {
        margin-left: calc(var(--gutter) * -1);
        margin-right: calc(var(--gutter) * -1);
        max-width: 100vw;
      }
    }
  }

  &.row-two {
    @include mq($until: tablet) {
      padding: calc(var(--spacing) * 3) 0;
    }

    @include mq($from: tablet) {
      .row-md {
        flex-direction: column;
        justify-content: center;
        align-items: center;
        height: 100%;
        gap: calc(var(--spacing) * 3);
        padding: calc(var(--spacing) * 2);

        > * {
          flex: 0 1 auto;
          max-width: 100%;
        }
      }
    }
  }
}
</style>
